import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { newCommandId } from "@/lib/ids";
import { adminApi, type AdminAuditFilters, type SharedRunFilters, type UserFilters } from "./adminApi";
import { ApiError } from "./errors";
import { qk } from "./queryKeys";
import { testerApi, type ArtifactFilters, type AuditFilters, type RunListFilters } from "./testerApi";
import type { AiSettingsInput, EnhanceRequest, JiraIssueRequest, JiraSettingsInput, Role, RunRequest, RunShare } from "./types";

export const useEnvironment = (opts: { poll?: boolean } = {}) =>
  useQuery({
    queryKey: qk.environment(),
    queryFn: testerApi.environment,
    refetchInterval: (q) => (opts.poll || q.state.data?.checks.some((c) => c.status === "RUNNING") ? 2_000 : false),
  });
export const useCatalogTests = (enabled = true) => useQuery({ queryKey: qk.catalogTests(), queryFn: testerApi.catalogTests, enabled });
export const useCatalogTest = (id: string) => useQuery({ queryKey: qk.catalogTest(id), queryFn: () => testerApi.catalogTest(id) });
export const useProfiles = () => useQuery({ queryKey: qk.profiles(), queryFn: testerApi.catalogProfiles });
export const useProfile = (id: string) =>
  useQuery({ queryKey: qk.profile(id), queryFn: () => testerApi.catalogProfile(id), enabled: id !== "" });
export const useReleases = (agent: string) =>
  useQuery({ queryKey: qk.releases(agent), queryFn: () => testerApi.releases(agent), staleTime: 600_000 });
export const useArtifacts = (f: ArtifactFilters = {}) =>
  useQuery({ queryKey: qk.artifacts(f), queryFn: () => testerApi.artifacts(f), placeholderData: keepPreviousData });
export const useArtifact = (id: string | undefined) =>
  useQuery({ queryKey: qk.artifact(id ?? ""), queryFn: () => testerApi.artifact(id ?? ""), enabled: !!id });
export const useResolution = (id: string | undefined) =>
  useQuery({
    queryKey: qk.resolution(id ?? ""),
    queryFn: () => testerApi.resolution(id ?? ""),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && q.state.data.status !== "DOWNLOADING" ? false : 1_000),
  });
export const useRuns = (f: RunListFilters, enabled = true) =>
  useQuery({ queryKey: qk.runs(f), queryFn: () => testerApi.runs(f), placeholderData: keepPreviousData, enabled });
export const useRun = (id: string, live = false, liveMs = 30_000) =>
  useQuery({ queryKey: qk.run(id), queryFn: () => testerApi.run(id), enabled: id !== "", refetchInterval: live ? liveMs : false });
export const useRunEvents = (id: string, enabled = true, refetchMs: number | false = false) =>
  useQuery({
    queryKey: qk.runEvents(id),
    queryFn: () => testerApi.runEvents(id),
    enabled,
    staleTime: Infinity,
    refetchInterval: refetchMs,
  });

const syncing = (s?: RunShare) =>
  !!s && s.enabled && !s.complete && (s.state === "PENDING" || s.state === "SYNCING" || s.state === "SYNCED");
export const useRunShare = (id: string, enabled = true) =>
  useQuery({
    queryKey: qk.runShare(id),
    queryFn: () => testerApi.runShare(id),
    enabled: enabled && id !== "",
    refetchInterval: (q) => (syncing(q.state.data) ? 5_000 : false),
  });
export function useShareRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => testerApi.shareRun(id),
    onSuccess: (data) => qc.setQueryData(qk.runShare(id), data),
  });
}
export const useSharedRuns = (f: Omit<SharedRunFilters, "cursor"> = {}, enabled = true) =>
  useInfiniteQuery({
    queryKey: qk.sharedRuns(f),
    queryFn: ({ pageParam }) => adminApi.sharedRuns({ ...f, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.next_cursor ?? undefined,
    enabled,
    retry: false,
    refetchInterval: 15_000,
  });
export const useSharedRunInfo = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: qk.sharedRun(id),
    queryFn: () => adminApi.sharedRun(id),
    enabled,
    retry: false,
    refetchInterval: (q) => (q.state.data && !q.state.data.complete ? 5_000 : false),
  });
export const useExecution = (id: string, live = false) =>
  useQuery({ queryKey: qk.execution(id), queryFn: () => testerApi.execution(id), refetchInterval: live ? 5_000 : false });
export const useRequests = (id: string, f: { route?: string; limit?: number; cursor?: string } = {}, live = false) =>
  useQuery({
    queryKey: qk.requests(id, f),
    queryFn: () => testerApi.requests(id, f),
    refetchInterval: live ? 3_000 : false,
    placeholderData: keepPreviousData,
  });
export const useRecordedRequest = (id: string, requestId: string | undefined) =>
  useQuery({
    queryKey: qk.request(id, requestId ?? ""),
    queryFn: () => testerApi.request(id, requestId ?? ""),
    enabled: !!requestId,
  });
export const useLogs = (id: string, f: { source: "agent" | "application"; tail?: number; q?: string }, live = false) =>
  useQuery({ queryKey: qk.logs(id, f), queryFn: () => testerApi.logs(id, f), refetchInterval: live ? 2_000 : false });
export const useStats = (range: "7d" | "30d" | "90d") => useQuery({ queryKey: qk.stats(range), queryFn: () => testerApi.stats(range) });
export const useLocalAudit = (f: AuditFilters, enabled = true) =>
  useQuery({ queryKey: qk.localAudit(f), queryFn: () => testerApi.audit(f), enabled, placeholderData: keepPreviousData });
export const useReport = (id: string) =>
  useQuery({ queryKey: qk.report(id), queryFn: () => testerApi.reportJson(id), staleTime: Infinity, refetchOnWindowFocus: false });

export const useReportMarkdown = (id: string, enabled = true) =>
  useQuery({
    queryKey: qk.reportMarkdown(id),
    queryFn: () => testerApi.reportMarkdown(id),
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
export const useBugReport = (id: string, enabled = true) =>
  useQuery({
    queryKey: qk.bugReport(id),
    queryFn: () => testerApi.bugReport(id),
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

export const useMe = (enabled = true) => useQuery({ queryKey: qk.me(), queryFn: adminApi.me, enabled, retry: false });
export const useUsers = (f: UserFilters, enabled = true) =>
  useQuery({ queryKey: qk.users(f), queryFn: () => adminApi.users(f), enabled, placeholderData: keepPreviousData });
export const useUser = (id: string) => useQuery({ queryKey: qk.user(id), queryFn: () => adminApi.user(id) });
export const useUsersSummary = (enabled = true) => useQuery({ queryKey: qk.usersSummary(), queryFn: adminApi.summary, enabled });
export const useAdminAudit = (f: AdminAuditFilters, enabled = true) =>
  useQuery({ queryKey: qk.adminAudit(f), queryFn: () => adminApi.audit(f), enabled, placeholderData: keepPreviousData });

async function firstProfileId(qc: QueryClient): Promise<string> {
  const id = (await qc.fetchQuery({ queryKey: qk.profiles(), queryFn: testerApi.catalogProfiles }))[0]?.id;
  if (!id) throw new ApiError(404, "NOT_FOUND", "This ATS release has no test profile.", "");
  return id;
}

export function useOcSanity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (profileId?: string) => testerApi.runOcSanity(profileId ?? (await firstProfileId(qc))),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.environment() }),
  });
}

export function useResolveArtifact() {
  return useMutation({ mutationFn: testerApi.resolveArtifact });
}

export function usePreviewRun() {
  return useMutation({ mutationFn: (body: RunRequest) => testerApi.previewRun(body) });
}

export function useStartRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RunRequest) => testerApi.startRun({ ...body, command_id: newCommandId() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["runs"] }),
  });
}

export function useCancelRun(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => testerApi.cancelRun(runId, newCommandId()),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.run(runId) }),
  });
}

function useUserMutation<A extends unknown[]>(fn: (...args: A) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: A) => fn(...args),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      void qc.invalidateQueries({ queryKey: ["audit", "admin"] });
    },
  });
}

export const useAdmit = () => useUserMutation((id: string, role: Role) => adminApi.admit(id, role));
export const useReject = () => useUserMutation((id: string) => adminApi.reject(id));
export const useChangeRole = () => useUserMutation((id: string, role: Role) => adminApi.changeRole(id, role));
export const useDeactivate = () => useUserMutation((id: string) => adminApi.deactivate(id));
export const useReactivate = () => useUserMutation((id: string, role?: Role) => adminApi.reactivate(id, role));

export const useAiSettings = (enabled = true) =>
  useQuery({ queryKey: qk.aiSettings(), queryFn: testerApi.aiSettings, enabled, retry: false, staleTime: 0 });

export function useSaveAiSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AiSettingsInput) => testerApi.saveAiSettings(body),
    onSuccess: (saved) => qc.setQueryData(qk.aiSettings(), saved),
  });
}

export function useDeleteAiSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: testerApi.deleteAiSettings,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.aiSettings() }),
  });
}

export function useTestAiSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: testerApi.testAiSettings,
    onSettled: () => qc.invalidateQueries({ queryKey: qk.aiSettings() }),
  });
}

export function useEnhance() {
  return useMutation({ mutationFn: (body: EnhanceRequest) => testerApi.enhance(body) });
}

export const useJiraSettings = (enabled = true) =>
  useQuery({ queryKey: qk.jiraSettings(), queryFn: testerApi.jiraSettings, enabled, retry: false, staleTime: 0 });

export function useSaveJiraSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: JiraSettingsInput) => testerApi.saveJiraSettings(body),
    onSuccess: (saved) => {
      qc.setQueryData(qk.jiraSettings(), saved);
      void qc.invalidateQueries({ queryKey: ["integrations", "jira"] });
    },
  });
}

export function useDeleteJiraSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: testerApi.deleteJiraSettings,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.jiraSettings() });
      void qc.removeQueries({ queryKey: ["integrations", "jira"] });
    },
  });
}

export function useTestJiraSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: testerApi.testJiraSettings,
    onSettled: () => qc.invalidateQueries({ queryKey: qk.jiraSettings() }),
  });
}

export const useJiraProjects = (q: string, enabled = true) =>
  useQuery({ queryKey: qk.jiraProjects(q), queryFn: () => testerApi.jiraProjects(q), enabled, retry: false, staleTime: 60_000 });

export const useJiraIssueTypes = (projectKey: string | undefined, enabled = true) =>
  useQuery({
    queryKey: qk.jiraIssueTypes(projectKey ?? ""),
    queryFn: () => testerApi.jiraIssueTypes(projectKey ?? ""),
    enabled: enabled && !!projectKey,
    retry: false,
    staleTime: 60_000,
  });

export function useCreateJiraIssue() {
  return useMutation({
    mutationFn: (v: { body: JiraIssueRequest; idempotencyKey: string }) => testerApi.createJiraIssue(v.body, v.idempotencyKey),
  });
}
